import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Modal, Spinner, Row, Col, Card, Badge } from "react-bootstrap";
import { Statistic, Progress, Tag } from "antd";
import {
  UserOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  TrophyOutlined,
  WarningOutlined,
  StarOutlined,
} from "@ant-design/icons";
import { fetchUserStatistics } from "@/store/adminApi/adminThunk";
import formatDate from "@/utils/formatDate";

const UserStatisticsModal = ({ show, onHide, userId }) => {
  const dispatch = useDispatch();
  const { userStatistics, statisticsLoading } = useSelector((state) => state.adminApi);

  useEffect(() => {
    if (show && userId) {
      dispatch(fetchUserStatistics(userId));
    }
  }, [show, userId, dispatch]);

  if (!userStatistics && !statisticsLoading) return null;

  const user = userStatistics?.user;
  const stats = userStatistics?.statistics;
  const activities = userStatistics?.recent_activities || [];

  const activityTypeMap = {
    login: "Tizimga kirdi",
    task_create: "Topshiriq yaratdi",
    task_complete: "Topshiriq bajardi",
    task_submit: "Topshiriq topshirdi",
    task_approve: "Topshiriq tasdiqladi",
    task_reject: "Topshiriq rad etdi",
    task_start: "Topshiriqni boshladi",
  };

  return (
    <Modal show={show} onHide={onHide} size="xl" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          <UserOutlined className="me-2" />
          Foydalanuvchi statistikasi
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {statisticsLoading ? (
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" />
          </div>
        ) : (
          <>
            {/* User Info */}
            <Card className="mb-4">
              <Card.Body>
                <Row className="align-items-center">
                  <Col xs="auto">
                    {user?.picture ? (
                      <img
                        src={user.picture}
                        alt={user.name}
                        className="rounded-circle"
                        style={{ width: "80px", height: "80px", objectFit: "cover" }}
                      />
                    ) : (
                      <div
                        className="bg-primary text-white rounded-circle d-flex align-items-center justify-content-center"
                        style={{ width: "80px", height: "80px", fontSize: "2rem" }}
                      >
                        {user?.name?.charAt(0) || "U"}
                      </div>
                    )}
                  </Col>
                  <Col>
                    <h4 className="mb-1">{user?.name || "N/A"}</h4>
                    <p className="text-muted mb-1">
                      <strong>Login:</strong> {user?.login || "N/A"}
                    </p>
                    {user?.phone && (
                      <p className="text-muted mb-1">
                        <strong>Telefon:</strong> {user.phone}
                      </p>
                    )}
                    {user?.roles && user.roles.length > 0 && (
                      <div>
                        {user.roles.map((role) => (
                          <Badge key={role.id} bg="info" className="me-1">
                            {role.name}
                          </Badge>
                        ))}
                      </div>
                    )}
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            {/* Statistics Overview */}
            <Row className="g-3 mb-4">
              <Col xs={12} sm={6} lg={3}>
                <Card className="h-100">
                  <Card.Body>
                    <Statistic
                      title="Jami topshiriqlar"
                      value={stats?.tasks_assigned || 0}
                      prefix={<CheckCircleOutlined />}
                      valueStyle={{ color: "#1890ff" }}
                    />
                  </Card.Body>
                </Card>
              </Col>
              <Col xs={12} sm={6} lg={3}>
                <Card className="h-100">
                  <Card.Body>
                    <Statistic
                      title="Bajarilgan"
                      value={stats?.tasks_completed || 0}
                      prefix={<CheckCircleOutlined />}
                      valueStyle={{ color: "#52c41a" }}
                    />
                  </Card.Body>
                </Card>
              </Col>
              <Col xs={12} sm={6} lg={3}>
                <Card className="h-100">
                  <Card.Body>
                    <Statistic
                      title="Muddati o'tgan"
                      value={stats?.tasks_overdue || 0}
                      prefix={<WarningOutlined />}
                      valueStyle={{ color: "#ff4d4f" }}
                    />
                  </Card.Body>
                </Card>
              </Col>
              <Col xs={12} sm={6} lg={3}>
                <Card className="h-100">
                  <Card.Body>
                    <Statistic
                      title="O'rtacha reyting"
                      value={stats?.avg_rating?.toFixed(1) || "N/A"}
                      prefix={<StarOutlined />}
                      suffix="/5"
                      valueStyle={{ color: "#faad14" }}
                    />
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            {/* Performance Metrics */}
            <Row className="g-3 mb-4">
              <Col xs={12} lg={6}>
                <Card className="h-100">
                  <Card.Body>
                    <h5 className="mb-3">Ishlash ko'rsatkichlari</h5>
                    <div className="mb-3">
                      <p className="text-muted mb-1">Bajarilish foizi</p>
                      <Progress
                        percent={Math.round(stats?.completion_rate || 0)}
                        strokeColor="#52c41a"
                      />
                    </div>
                    {stats?.on_time_rate !== null && (
                      <div className="mb-3">
                        <p className="text-muted mb-1">Muddatida bajarilish foizi</p>
                        <Progress
                          percent={Math.round(stats?.on_time_rate || 0)}
                          strokeColor="#1890ff"
                        />
                      </div>
                    )}
                    <div className="mt-3">
                      <Statistic
                        title="O'rtacha bajarish vaqti"
                        value={stats?.avg_completion_time_hours?.toFixed(1) || "N/A"}
                        suffix="soat"
                      />
                    </div>
                  </Card.Body>
                </Card>
              </Col>

              <Col xs={12} lg={6}>
                <Card className="h-100">
                  <Card.Body>
                    <h5 className="mb-3">Topshiriqlar statuslari</h5>
                    <Row>
                      <Col xs={6} className="mb-3">
                        <Statistic
                          title="Kutilayotgan"
                          value={stats?.tasks_pending || 0}
                          valueStyle={{ fontSize: "1.2rem" }}
                        />
                      </Col>
                      <Col xs={6} className="mb-3">
                        <Statistic
                          title="Jarayonda"
                          value={stats?.tasks_in_progress || 0}
                          valueStyle={{ fontSize: "1.2rem" }}
                        />
                      </Col>
                      <Col xs={6} className="mb-3">
                        <Statistic
                          title="Topshirilgan"
                          value={stats?.tasks_submitted || 0}
                          valueStyle={{ fontSize: "1.2rem" }}
                        />
                      </Col>
                      <Col xs={6} className="mb-3">
                        <Statistic
                          title="Yaratgan"
                          value={stats?.tasks_created || 0}
                          valueStyle={{ fontSize: "1.2rem" }}
                        />
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            {/* Recent Activity */}
            <Card>
              <Card.Body>
                <h5 className="mb-3">So'nggi faoliyatlar</h5>
                {activities.length > 0 ? (
                  <div style={{ maxHeight: "300px", overflowY: "auto" }}>
                    {activities.map((activity, idx) => (
                      <div
                        key={activity.id || idx}
                        className="d-flex align-items-start mb-3 pb-3 border-bottom"
                      >
                        <div className="me-3">
                          <i
                            className="fa fa-circle text-primary"
                            style={{ fontSize: "0.5rem" }}
                          ></i>
                        </div>
                        <div className="flex-grow-1">
                          <p className="mb-1">
                            {activityTypeMap[activity.activity_type] ||
                              activity.activity_type}
                          </p>
                          <p className="text-muted mb-0 small">
                            {formatDate(activity.timestamp)}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted">Faoliyat mavjud emas</p>
                )}
              </Card.Body>
            </Card>

            {/* Period Statistics */}
            <Row className="g-3 mt-3">
              <Col xs={6}>
                <Card>
                  <Card.Body>
                    <Statistic
                      title="Bu hafta bajarilgan"
                      value={stats?.tasks_completed_this_week || 0}
                      prefix={<TrophyOutlined />}
                      valueStyle={{ color: "#722ed1" }}
                    />
                  </Card.Body>
                </Card>
              </Col>
              <Col xs={6}>
                <Card>
                  <Card.Body>
                    <Statistic
                      title="Bu oy bajarilgan"
                      value={stats?.tasks_completed_this_month || 0}
                      prefix={<TrophyOutlined />}
                      valueStyle={{ color: "#13c2c2" }}
                    />
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default UserStatisticsModal;
